import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from '@yema/commerce/StoreClient';
import { initializeApollo } from '@yema/commerce/lib/apollo';
import { initialize } from 'utils/initialize';
import initializeUserContext from 'utils/initializeUserContext';
import { get_ } from 'sf-modules';
import { getMenus } from 'functions/fnRequestAPIs/fnMenus';
import { getStaticContentSlugs, getStaticContent } from 'functions/fnRequestAPIs/fnCMSPagesAPI';
import NotFoundLayout from 'layouts/NotFound.layout';
import PageLayout from 'layouts/Page.layout';
import CMS_Page from 'components/cms/CMS_Page';

const StaticPage = ({ pageContent, staticContent }) => {
    const router = useRouter();
    const authToken = useSelector(({ authentication }) => authentication.token);
    const dispatch = useDispatch();

    useEffect(() => initializeUserContext({ authToken, dispatch }), [authToken, router.asPath]);

    if (!router.isFallback && (!pageContent || !pageContent.data || pageContent.error))
        return <NotFoundLayout staticContent={staticContent} />;

    return (
        <PageLayout staticContent={staticContent}>
            <CMS_Page loading={router.isFallback} content={get_(pageContent, ['data'])} />
        </PageLayout>
    );
};

export async function getStaticProps(context) {
    try {
        if (!context.params.staticPageSlug) return { notFound: true };

        const { reduxStore, apolloClient } = await initialize({
            withReduxStore: true,
            withApolloClient: true,
            withUserContext: false,
        });

        const menus = await getMenus(context, apolloClient);
        const page = await getStaticContent(
            apolloClient,
            context.params.staticPageSlug,
            context.preview
        );

        if (!page || !page.data) return { notFound: true };

        const metaTags = {
            title: get_(page, ['data', 'seoTitle']),
            description: get_(page, ['data', 'searchDescription']),
            image: get_(page, ['data', 'seoImage', 'file']),
            shouldIndex: get_(page, ['data', 'seoShouldIndex']),
            canonicalUrl: get_(page, ['data', 'seoCanonicalUrl']),
        };

        return {
            props: {
                initialReduxState: reduxStore.getState(),
                revalidatedOn: Date.now(),
                // initialApolloState: apolloClient.cache.extract(),
                pageContent: page,
                staticContent: {
                    ...menus,
                    metaTags,
                    preview: !!context.preview,
                },
            },

            // Re-generate the page:
            // - when a request comes in
            // - at most once every X seconds
            revalidate: 120,
        };
    } catch (error) {
        // The API most likely died
        console.error(error);
        return {
            props: {},
        };
    }
}

export async function getStaticPaths() {
    const apolloClient = initializeApollo();
    const allPages = await getStaticContentSlugs(apolloClient);
    return {
        paths: allPages?.map((slug) => `/sc/${slug}`) || [],
        fallback: 'blocking',
    };
}

export default StaticPage;
